// ============================================================
//  Gate-Status je Landschaft — gesetzt von Menschen, nicht gerechnet. Bleibt über Reload
//  erhalten (localStorage). Der Standard ist „offen"; ein gesetzter Status trägt Datum und
//  Namen dessen, der ihn gesetzt hat (Freitext). Reset zusammen mit dem RFP-Plan-Reset.
// ============================================================
import { useSyncExternalStore } from "react";
import { landscapes as rfpLandscapes } from "../data/landscapes";

export type GateStatus = "offen" | "in-pruefung" | "freigegeben" | "freigegeben-mit-auflage";

export interface GateStatusEntry {
  status: GateStatus;
  /** ISO-Datum der Statussetzung (nur bei Status ≠ offen sinnvoll). */
  datum?: string;
  /** Wer den Status gesetzt hat — Freitext. */
  von?: string;
}

export type GateStatusMap = Record<string, GateStatusEntry>;

export const GATE_STATUS_KEY = "rise-cockpit.gate-status.v1";
export const GATE_STATUS_VALUES: GateStatus[] = ["offen", "in-pruefung", "freigegeben", "freigegeben-mit-auflage"];
export const GATE_STATUS_LABEL: Record<GateStatus, string> = {
  offen: "offen",
  "in-pruefung": "in Prüfung",
  freigegeben: "freigegeben",
  "freigegeben-mit-auflage": "freigegeben mit Auflage",
};
export const GATE_STATUS_TONE: Record<GateStatus, "neutral" | "amber" | "green"> = {
  offen: "neutral",
  "in-pruefung": "amber",
  freigegeben: "green",
  "freigegeben-mit-auflage": "green",
};

const OFFEN: GateStatusEntry = { status: "offen" };
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const knownIds = new Set(rfpLandscapes.map((l) => l.id));
const isStatus = (v: unknown): v is GateStatus => typeof v === "string" && (GATE_STATUS_VALUES as string[]).includes(v);

function sanitize(id: string, v: unknown): GateStatusEntry | null {
  if (!knownIds.has(id) || !v || typeof v !== "object") return null;
  const e = v as Partial<GateStatusEntry>;
  if (!isStatus(e.status)) return null;
  const out: GateStatusEntry = { status: e.status };
  if (typeof e.datum === "string" && ISO.test(e.datum)) out.datum = e.datum;
  if (typeof e.von === "string" && e.von.trim()) out.von = e.von.trim().slice(0, 80);
  return out;
}

function load(): GateStatusMap {
  try {
    const raw = localStorage.getItem(GATE_STATUS_KEY);
    if (!raw) return {};
    const j = JSON.parse(raw);
    if (!j || typeof j !== "object" || Array.isArray(j)) return {};
    const out: GateStatusMap = {};
    for (const [id, v] of Object.entries(j)) { const e = sanitize(id, v); if (e) out[id] = e; }
    return out;
  } catch { return {}; }
}
function persist() {
  try {
    if (Object.keys(entries).length === 0) localStorage.removeItem(GATE_STATUS_KEY);
    else localStorage.setItem(GATE_STATUS_KEY, JSON.stringify(entries));
  } catch { /* privat/blockiert: nur im Speicher */ }
}

let entries: GateStatusMap = load();
const listeners = new Set<() => void>();

function commit(next: GateStatusMap) {
  entries = next;
  persist();
  for (const l of listeners) l();
}
function subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }
const getSnapshot = () => entries;

export const gateStatusStore = {
  /** Status einer Landschaft; ohne Eintrag „offen". */
  get: (id: string): GateStatusEntry => entries[id] ?? OFFEN,
  /** Alle gesetzten Einträge — Kopie. */
  all: (): GateStatusMap => ({ ...entries }),
  /** Ist für diese Landschaft ein Status gesetzt (≠ offen ohne Angaben)? */
  isSet: (id: string): boolean => id in entries,
  /**
   * Setzt den Status. „offen" ohne Datum und Name löscht den Eintrag — der Standard braucht
   * keinen Speicherplatz. Ungültige Landschaften und Werte werden ignoriert.
   */
  set(id: string, entry: GateStatusEntry) {
    const e = sanitize(id, entry);
    if (!e) return;
    const next = { ...entries };
    if (e.status === "offen" && !e.datum && !e.von) delete next[id]; else next[id] = e;
    commit(next);
  },
  clear(id: string) {
    if (!(id in entries)) return;
    const next = { ...entries }; delete next[id]; commit(next);
  },
  reset() { if (Object.keys(entries).length) commit({}); },
};

/** Reaktiv: alle Statuseinträge (Referenz wechselt bei jeder Änderung). */
export function useGateStatusMap(): GateStatusMap {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Reaktiv: Status einer Landschaft. */
export function useGateStatus(id: string): GateStatusEntry {
  const all = useGateStatusMap();
  return all[id] ?? OFFEN;
}
